import React, { createContext, useCallback, useState } from 'react';
import { useConfig } from '@/context/config';
import { toast } from 'react-toastify';
import { ApiService, GenerateTokenPayload } from '@/lib/api';
import type { ConnectionContextType } from './types';
import { DEFAULT_AGENT_NAME } from '@/constants';

export const ConnectionContext = createContext<ConnectionContextType>({
  shouldConnect: false,
  wsUrl: '',
  token: '',
  connect: async () => {},
  disconnect: async () => {},
  setToken: () => {},
});

export function ConnectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { config } = useConfig();
  const [connectionDetails, setConnectionDetails] = useState({
    shouldConnect: false,
    wsUrl: import.meta.env.VITE_LIVEKIT_URL || '',
    token: '',
  });

  const connect = useCallback(
    async (sessionToken: string, metadata: string) => {
      let token = '';
      let url = '';

      try {
        if (!import.meta.env.VITE_LIVEKIT_URL) {
          throw new Error('VITE_LIVEKIT_URL is not set');
        }

        url = import.meta.env.VITE_LIVEKIT_URL;

        const body: GenerateTokenPayload = {
          agentName: DEFAULT_AGENT_NAME,
          metadata,
        };
        if (config.settings.roomName) {
          body.roomName = config.settings.roomName;
        }
        if (config.settings.participantId) {
          body.participantId = config.settings.participantId;
        }
        if (config.settings.participantName) {
          body.participantName = config.settings.participantName;
        }
        if (config.settings.agentName) {
          body.agentName = config.settings.agentName;
        }
        if (config.settings.sessionToken) {
          body.sessionToken = config.settings.sessionToken;
        }

        const attributesArray = Array.isArray(config.settings.attributes)
          ? config.settings.attributes
          : [];
        if (attributesArray?.length) {
          const attributes = attributesArray.reduce(
            (
              acc: Record<string, string>,
              attr: { key?: string; value?: string },
            ) => {
              if (attr.key) {
                acc[attr.key] = attr.value ?? '';
              }
              return acc;
            },
            {} as Record<string, string>,
          );
          body.attributes = attributes;
        }

        const response = await ApiService.Session.generateToken(
          sessionToken,
          body,
        );
        if (response.success && response.value) {
          token = response.value.data?.accessToken ?? '';
        } else if (response.failure) {
          throw new Error(response.message ?? 'Failed to generate token');
        } else {
          throw new Error('Failed to generate token');
        }

        setConnectionDetails({ wsUrl: url, token, shouldConnect: true });
      } catch (error) {
        console.error('Connection error:', error);
        toast.error('Failed to establish connection');
        setConnectionDetails(prev => ({ ...prev, shouldConnect: false }));
      }
    },
    [config.settings],
  );

  const disconnect = useCallback(async () => {
    setConnectionDetails(prev => ({ ...prev, shouldConnect: false }));
  }, []);

  const setToken = useCallback((token: string) => {
    setConnectionDetails(prev => ({
      ...prev,
      token,
    }));
  }, []);

  return (
    <ConnectionContext.Provider
      value={{
        wsUrl: connectionDetails.wsUrl,
        token: connectionDetails.token,
        shouldConnect: connectionDetails.shouldConnect,
        connect,
        disconnect,
        setToken,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
}
